import React from 'react';
import clsx from 'clsx';

import Button from '@smart-tv/components/button/button';
import Image from '@smart-tv/components/image/image';
import Typography from '@smart-tv/components/typography';
import type { FeatureIntro } from '@smart-tv/typings/content';

import styles from './feature-intro.module.scss';

interface Props {
  step: FeatureIntro['steps'][number];
  stepNumber: number;
  totalSteps: number;
  isActive: boolean;
  player?: boolean;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
}

export const getFeatureIntroButtonFocusKey = (stepId: string, button: 'next' | 'back' | 'close') =>
  `feature-intro-step-${stepId}-${button}-button`;

export const FeatureIntroStep: React.FC<Props> = (props: Props) => {
  const { step, stepNumber, totalSteps, isActive, player, onNext, onBack, onClose } = props;

  const hasNextStep = stepNumber < totalSteps;
  const hasPreviousStep = stepNumber > 1;
  const isSingleStep = totalSteps === 1;

  return (
    <div className={styles['feature-intro-step']} aria-hidden={!isActive}>
      {step.media && !player && (
        <Image
          className={styles['feature-intro-step__image']}
          src={step.media.mediaUrl}
          alt={`Step ${stepNumber} image`}
          visibleByDefault={isActive}
        />
      )}
      <div className={styles['feature-intro-step__content']}>
        <div className={styles['feature-intro-step__text']}>
          {step.subHeadline && (
            <Typography variant="caption" className={styles['feature-intro-step__subheadline']}>
              {step.subHeadline}
            </Typography>
          )}
          <Typography
            variant="h2"
            className={clsx(styles['feature-intro-step__headline'], {
              [styles['feature-intro-step__headline--front-page']]: !player,
              [styles['feature-intro-step__headline--player']]: player
            })}
          >
            {step.headline}
          </Typography>
          <div className={styles['feature-intro-step__description-row']}>
            {step.media && player && (
              <Image
                className={styles['feature-intro-step__icon']}
                src={step.media.mediaUrl}
                alt={`Step ${stepNumber} image`}
                visibleByDefault={isActive}
              />
            )}
            <Typography
              className={clsx(styles['feature-intro-step__description'], {
                [styles['feature-intro-step__description--clamped']]: player
              })}
            >
              {step.description}
            </Typography>
          </div>
          {!isSingleStep && (
            <Typography
              variant="caption2"
              color="textSecondary"
              className={styles['feature-intro-step__progress']}
            >
              Step {stepNumber}/{totalSteps}
            </Typography>
          )}
        </div>
        <div className={styles['feature-intro-step__buttons']}>
          {!isSingleStep && (
            <>
              <Button
                focusKey={getFeatureIntroButtonFocusKey(step.id, 'next')}
                className={styles['feature-intro-step__button']}
                disabled={!isActive || !hasNextStep}
                onClick={onNext}
              >
                Næste
              </Button>
              <Button
                focusKey={getFeatureIntroButtonFocusKey(step.id, 'back')}
                className={styles['feature-intro-step__button']}
                disabled={!isActive || !hasPreviousStep}
                onClick={onBack}
              >
                Tilbage
              </Button>
            </>
          )}
          <Button
            focusKey={getFeatureIntroButtonFocusKey(step.id, 'close')}
            className={styles['feature-intro-step__button']}
            disabled={!isActive}
            onClick={onClose}
          >
            Luk
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FeatureIntroStep);
